import React, { createContext, useState, useContext, useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import { EmptyState } from "@opendate-app/setlist";
import { ToastContainer } from "react-toastify";

import axios from "axios";

import MarketingSettingsIndex from "./MarketingSettingsIndex";
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const MarketingSettingsContext = createContext();

export function useMarketingSettingsContext() {
  return useContext(MarketingSettingsContext);
}

const MarketingSettingsProvider = (props) => {
  const venueOwnershipIdParam = new URLSearchParams(window.location.search).get("venue_ownership_id");

  const {
    csrfToken,
    team,
    teamMembership,
    enforceMarketingVenueAccessControl,
    selectedMarketingVenueOwnershipId,
    lastTouchedVenueOwnership
  } = props;

  const [venueOwnerships, setVenueOwnerships] = useState(props.venueOwnerships);
  const [venueOwnership, setVenueOwnership] = useState(() => {
    const requested = venueOwnerships.find((ownership) => ownership.id === parseInt(venueOwnershipIdParam));
    if (requested) return requested;

    if (enforceMarketingVenueAccessControl) {
      if (selectedMarketingVenueOwnershipId) {
        const selected = venueOwnerships.find((ownership) => ownership.id === selectedMarketingVenueOwnershipId);
        if (selected) return selected;
      }
      return venueOwnerships[0];
    }

    return lastTouchedVenueOwnership || venueOwnerships[0];
  });
  const [sendDomains, setSendDomains] = useState(venueOwnership.send_domains);
  const [formState, setFormState] = useState({});
  const [logoThumbUrl, setLogoThumbUrl] = useState(venueOwnership.logo_thumb_url);
  const [logoFile, setLogoFile] = useState(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const shownVenueOwnershipId = useRef(venueOwnership.id);
  const logoDraft = useRef(0);
  const [cloudflareCustomHostnames, setCloudflareCustomHostnames] = useState(props.cloudflareCustomHostnames);
  const extractFormState = (venueOwnership) => {
    setFormState({
      time_zone: venueOwnership.time_zone,
      facebook_url: venueOwnership.facebook_url || "",
      twitter_url: venueOwnership.twitter_url || "",
      instagram_url: venueOwnership.instagram_url || "",
      linkedin_url: venueOwnership.linkedin_url || "",
      primary_color_hex: venueOwnership.primary_color_hex || "",
      secondary_color_hex: venueOwnership.secondary_color_hex || "",
      font_family: venueOwnership.font_family || "",
      marketing_alert_emails: venueOwnership.marketing_alert_emails || "",
      upcoming_events_sort_column: venueOwnership.upcoming_events_sort_column || "start_time",
      upcoming_events_sort_order: venueOwnership.upcoming_events_sort_order || "asc",
      just_announced_events_sort_column: venueOwnership.just_announced_events_sort_column || "announce_time",
      just_announced_events_sort_order: venueOwnership.just_announced_events_sort_order || "desc",
    });
  };

  useLayoutEffect(() => {
    extractFormState(venueOwnership);
    setSendDomains(venueOwnership.send_domains);
    setLogoThumbUrl(venueOwnership.logo_thumb_url);
    setLogoFile(null);
    setLogoRemoved(false);
    shownVenueOwnershipId.current = venueOwnership.id;
  }, [venueOwnership.id]);

  const chooseLogo = (file) => {
    logoDraft.current += 1;
    const draft = logoDraft.current;
    const venueOwnershipId = shownVenueOwnershipId.current;
    setLogoFile(file);
    setLogoRemoved(false);

    const reader = new FileReader();
    reader.onloadend = () => {
      if (logoDraft.current === draft && shownVenueOwnershipId.current === venueOwnershipId) {
        setLogoThumbUrl(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    logoDraft.current += 1;
    setLogoFile(null);
    setLogoRemoved(true);
    setLogoThumbUrl("");
  };

  const updateVenueOwnership = (id, changes) => {
    const merge = (ownership) => ({ ...ownership, ...(typeof changes === "function" ? changes(ownership) : changes) });
    setVenueOwnership((current) => (current.id === id ? merge(current) : current));
    setVenueOwnerships((current) => current.map((ownership) => (ownership.id === id ? merge(ownership) : ownership)));
  };

  const updateSendDomains = (id, change) => {
    if (shownVenueOwnershipId.current === id) {
      setSendDomains(change);
    }
    updateVenueOwnership(id, (ownership) => ({ send_domains: change(ownership.send_domains || []) }));
  };

  const sendDomainModified = (sendDomain) => {
    updateSendDomains(venueOwnership.id, (domains) => domains.map((domain) => (domain.id === sendDomain.id ? sendDomain : domain)));
  };

  const sendDomainCreated = (venueOwnership, sendDomain) => {
    updateSendDomains(venueOwnership.id, (domains) => domains.filter((domain) => domain.id).concat([sendDomain]));
  };

  const sendDomainAdded = () => {
    setSendDomains([{
      domain: "",
      from_address: "",
      verified: false,
      send_domain_dns_records: [],
    }])
  }

  const updateMarketingSettings = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    const savedVenueOwnershipId = venueOwnership.id;
    const sentLogoDraft = logoDraft.current;

    const formData = new FormData();
    Object.keys(formState).forEach(key => {
      formData.append(`venue_ownership[${key}]`, formState[key]);
    });

    if (logoFile) {
      formData.append("venue_ownership[logo]", logoFile);
    }

    if (logoRemoved) {
      formData.append("venue_ownership[destroy_logo]", "true");
    }

    axios.put(`/teams/${team.id}/venue_ownerships/${venueOwnership.id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
      .then(({ data }) => {
        updateVenueOwnership(venueOwnership.id, { ...formState, ...data });
        if (shownVenueOwnershipId.current === savedVenueOwnershipId && logoDraft.current === sentLogoDraft) {
          setLogoFile(null);
          setLogoRemoved(false);
          setLogoThumbUrl(data.logo_thumb_url);
        }

        toastSuccess("Marketing settings updated successfully");
      })
      .catch((error) => {
        if (error.response && Array.isArray(error.response.data)) {
          toastError(error.response.data.join(", "));
        } else {
          toastError("Could not update marketing settings");
        }
      });

    sendDomains.filter((sd) => sd.id).forEach((sendDomain) => {
      updateSendDomain(sendDomain);
    });
  };

  const updateSendDomain = (sendDomain) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`/teams/${team.id}/venue_ownerships/${venueOwnership.id}/send_domains/${sendDomain.id}`, {
      send_domain: {
        from_address: sendDomain.from_address,
        name: sendDomain.name,
      }
    })
      .then(({ data }) => {
        sendDomainModified(data);
        toastSuccess("Send domain updated successfully");
      })
      .catch(({ response }) => {
        if (response && response.data && Array.isArray(response.data.errors)) {
          toastError(response.data.errors.join(", "));
        } else {
          toastError("Could not update send domain");
        }
      });
  };

  const value = {
    ...props,
    venueOwnerships,
    venueOwnership,
    setVenueOwnership,
    updateVenueOwnership,
    updateSendDomains,
    sendDomains,
    setSendDomains,
    formState,
    setFormState,
    sendDomainCreated,
    sendDomainModified,
    sendDomainAdded,
    updateMarketingSettings,
    logoFile,
    chooseLogo,
    removeLogo,
    logoThumbUrl,
    setLogoThumbUrl,
    updateSendDomain,
    cloudflareCustomHostnames,
    setCloudflareCustomHostnames,
  };

  return (
    <MarketingSettingsContext.Provider value={value}>
      {props.children}
    </MarketingSettingsContext.Provider>
  );
};

const MarketingSettingsIndexContainer = (props) => {
  if (props.venueOwnerships.length === 0) {
    return (
      <EmptyState
        title="You don't have access to any venues"
        description="Ask a team admin to give you access to a venue to manage its marketing settings."
      />
    );
  }

  return (
    <MarketingSettingsProvider {...props}>
      <ToastContainer icon={false} />
      <MarketingSettingsIndex />
    </MarketingSettingsProvider>
  );
};

MarketingSettingsIndexContainer.propTypes = {
  venueOwnerships: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default MarketingSettingsIndexContainer;
